import { Button, Typography } from '@/components/common';
import { Textarea } from '@/components/form';
import { errorFieldMessage } from '@/const';
import { usePdfExtractAll } from '@/hooks';
import clsx from 'clsx';
import * as React from 'react';
import {
	FieldValues,
	FormState,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import { ConfigureDocumentTabs } from './configure-document';

interface SendDocumentFormProps {
	isLoading: boolean;
	file: File | null;
	setFile: React.Dispatch<React.SetStateAction<File | null>>;
	setValue: UseFormSetValue<FieldValues>;
	formState: FormState<FieldValues>;
	register: UseFormRegister<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	showMessageInput: boolean;
	showPreviewPage: () => void;
	hideModalUploadDocument: () => void;
	handleContinue: () => void;
	submitLabel?: string;
}

const SendDocumentForm: React.FC<SendDocumentFormProps> = ({
	isLoading,
	file,
	setFile,
	formState,
	setValue,
	watch,
	register,
	showPreviewPage,
	hideModalUploadDocument,
	showMessageInput,
	handleContinue,
	submitLabel = 'Send Document',
}) => {
	const { errors, isValid } = formState;

	// Extraction is slow; start it on upload so the mobile preview is ready later.
	usePdfExtractAll(file);

	const rules = {
		document_name: {
			required: { value: true, message: errorFieldMessage },
			maxLength: {
				value: 255,
				message: 'Document name cannot exceed 255 characters.',
			},
		},
	};

	return (
		<div className="flex h-full flex-col">
			<div className="flex-1 space-y-3 overflow-y-auto scroll-custom pb-1">
				{showMessageInput ? (
					<Textarea
						title="Insert Message"
						additionalInfoTitle="(Optional)"
						name="message"
						placeholder="Write the details here"
						register={register}
						rows={8}
					/>
				) : (
					<ConfigureDocumentTabs
						file={file}
						setFile={setFile}
						register={register}
						setValue={setValue}
						watch={watch}
						documentNameError={errors.document_name}
						documentNameRules={rules.document_name}
					/>
				)}
			</div>
			<div className="flex shrink-0 items-center gap-x-2 justify-between mt-5">
				{showMessageInput && (
					<>
						<Button
							label="Preview"
							size="fit"
							decoration="line-primary"
							disabled={!isValid || !file}
							className="!h-12"
							onClick={() => {
								hideModalUploadDocument();
								showPreviewPage();
							}}
						/>
						<Button
							label={submitLabel}
							type="submit"
							size="fit"
							decoration="primary"
							loading={isLoading}
							disabled={isLoading}
							className={clsx({
								'!bg-[#B18EE0] hover:!bg-[#B18EE0] focus:!bg-[#B18EE0] active:!bg-[#B18EE0] !border-[#B18EE0] !h-12':
									!isValid || !file,
							})}
						/>
					</>
				)}

				{!showMessageInput && (
					<div className="w-full">
						<Button
							type="button"
							size="full"
							decoration="primary"
							loading={isLoading}
							disabled={isLoading || !isValid || !file}
							onClick={handleContinue}
							className={clsx({
								'!bg-[#B18EE0] hover:!bg-[#B18EE0] focus:!bg-[#B18EE0] active:!bg-[#B18EE0] !border-[#B18EE0] !h-12':
									!isValid || !file,
							})}
						>
							Continue
						</Button>
						{(!isValid || !file) && (
							<Typography
								type="regular-12"
								className="text-gray-30 text-center mt-2"
							>
								Upload a document to continue
							</Typography>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default SendDocumentForm;
